/**
 * ISO 8601 week utilities (Monday-start weeks, week 1 = the week containing
 * the year's first Thursday). Mirrors the backend's `ReportingPeriod` weekly
 * arithmetic in `dashboards/domain.py`.
 *
 * Every date these functions build or step is a UTC-midnight timestamp used
 * purely to carry a calendar date (year/month/day) - not a real-world
 * instant - so all reads must go through the UTC getters too, never the
 * local ones, or the calendar date silently shifts by a day depending on
 * the viewer's timezone. Turning a real "now" into a calendar date for
 * "current week" purposes is `londonToday()`'s job, matching the backend's
 * chosen timezone (see its docstring).
 */

export interface IsoWeek {
  isoYear: number;
  isoWeek: number;
}

function toUtcDate(year: number, monthIndex: number, day: number): Date {
  return new Date(Date.UTC(year, monthIndex, day));
}

/**
 * The ISO year + week for a given date, read from its UTC calendar
 * components. Every other function in this module builds/steps dates as a
 * pure UTC-midnight calendar date with no real-world timezone meaning, so
 * reading a date back with local getters here (as opposed to UTC ones)
 * would silently shift it by a day for `addIsoWeeks`/`isoWeekToMonday`
 * output in any timezone west of UTC.
 *
 * To get "today" in a specific real-world timezone (e.g. to match the
 * backend's Europe/London "current week" convention), convert first with
 * `londonToday()` rather than passing `new Date()` directly - a raw
 * `new Date()` read via UTC components gives *UTC's* current calendar date,
 * not the viewer's or London's.
 */
export function getIsoWeek(date: Date): IsoWeek {
  const d = toUtcDate(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  const dayNum = d.getUTCDay() || 7; // Sunday (0) -> 7
  d.setUTCDate(d.getUTCDate() + 4 - dayNum); // Thursday of this ISO week decides the ISO year
  const isoYear = d.getUTCFullYear();
  const yearStart = toUtcDate(isoYear, 0, 1);
  const isoWeek = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return { isoYear, isoWeek };
}

/** The Monday that starts a given ISO week. */
export function isoWeekToMonday(isoYear: number, isoWeek: number): Date {
  const jan4 = toUtcDate(isoYear, 0, 4);
  const jan4Day = jan4.getUTCDay() || 7;
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - jan4Day + 1);
  const monday = new Date(week1Monday);
  monday.setUTCDate(week1Monday.getUTCDate() + (isoWeek - 1) * 7);
  return monday;
}

/** (Monday, Sunday) date range for a given ISO week. */
export function isoWeekDateRange(isoYear: number, isoWeek: number): [Date, Date] {
  const monday = isoWeekToMonday(isoYear, isoWeek);
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  return [monday, sunday];
}

/** Number of ISO weeks in a given ISO year (52 or 53). */
export function isoWeeksInYear(isoYear: number): number {
  const isLeap = (y: number) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
  const dayOfWeek = toUtcDate(isoYear, 0, 1).getUTCDay();
  return dayOfWeek === 4 || (dayOfWeek === 3 && isLeap(isoYear)) ? 53 : 52;
}

/** Step forward/back by `delta` ISO weeks, handling year rollover and 52/53-week years. */
export function addIsoWeeks(isoYear: number, isoWeek: number, delta: number): IsoWeek {
  const monday = isoWeekToMonday(isoYear, isoWeek);
  monday.setUTCDate(monday.getUTCDate() + delta * 7);
  return getIsoWeek(monday);
}

/** Short label, e.g. "2026-W23". */
export function formatIsoWeekLabel(isoYear: number, isoWeek: number): string {
  return `${isoYear}-W${String(isoWeek).padStart(2, '0')}`;
}

/** The week's date range as a short label, e.g. "2 Jun to 8 Jun". */
export function formatIsoWeekRangeLabel(isoYear: number, isoWeek: number): string {
  const [monday, sunday] = isoWeekDateRange(isoYear, isoWeek);
  const fmt = (d: Date) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  return `${fmt(monday)} to ${fmt(sunday)}`;
}

/** Combined label used in tables/pickers: "2026-W23 (2 Jun to 8 Jun)". */
export function formatIsoWeekFullLabel(isoYear: number, isoWeek: number): string {
  return `${formatIsoWeekLabel(isoYear, isoWeek)} (${formatIsoWeekRangeLabel(isoYear, isoWeek)})`;
}

/** Last ISO week of a calendar month (the week containing the month's last day). */
export function lastIsoWeekOfMonth(year: number, month: number): IsoWeek {
  const lastDay = new Date(Date.UTC(year, month, 0));
  return getIsoWeek(lastDay);
}

/** Calendar month of this ISO week's Thursday (the ISO week-year rule). */
export function isoWeekToCalendarMonth(isoYear: number, isoWeek: number): { year: number; month: number } {
  const monday = isoWeekToMonday(isoYear, isoWeek);
  const thursday = new Date(monday);
  thursday.setUTCDate(monday.getUTCDate() + 3);
  return { year: thursday.getUTCFullYear(), month: thursday.getUTCMonth() + 1 };
}

/** Whether any day of the ISO week falls in the given calendar month. */
export function isoWeekOverlapsMonth(isoYear: number, isoWeek: number, year: number, month: number): boolean {
  const [monday, sunday] = isoWeekDateRange(isoYear, isoWeek);
  const cursor = new Date(monday);
  while (cursor.getTime() <= sunday.getTime()) {
    if (cursor.getUTCFullYear() === year && cursor.getUTCMonth() + 1 === month) {
      return true;
    }
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return false;
}

/**
 * Recover the ISO year for `isoWeek` that belongs to this calendar month.
 * Mirrors `iso_year_for_calendar_month` in dashboards/domain.py.
 */
/** ISO weeks that overlap a calendar month, first to last. */
export function weeksOverlappingMonth(year: number, month: number): IsoWeek[] {
  const first = getIsoWeek(new Date(Date.UTC(year, month - 1, 1)));
  const last = lastIsoWeekOfMonth(year, month);
  const weeks: IsoWeek[] = [first];
  let cursor = first;
  while (cursor.isoYear !== last.isoYear || cursor.isoWeek !== last.isoWeek) {
    cursor = addIsoWeeks(cursor.isoYear, cursor.isoWeek, 1);
    weeks.push(cursor);
    if (weeks.length > 8) break;
  }
  return weeks;
}

export function isoYearForCalendarMonth(calendarYear: number, calendarMonth: number, isoWeek: number): number {
  const monthStart = new Date(Date.UTC(calendarYear, calendarMonth - 1, 1));
  const monthEnd = new Date(Date.UTC(calendarYear, calendarMonth, 0));
  const ranked: Array<{ score: number; year: number }> = [];
  for (const candidateYear of [calendarYear - 1, calendarYear, calendarYear + 1]) {
    if (isoWeek < 1 || isoWeek > isoWeeksInYear(candidateYear)) continue;
    const [monday, sunday] = isoWeekDateRange(candidateYear, isoWeek);
    if (monday.getTime() > monthEnd.getTime() || sunday.getTime() < monthStart.getTime()) continue;
    const thursday = new Date(monday);
    thursday.setUTCDate(monday.getUTCDate() + 3);
    const inMonth = thursday.getUTCFullYear() === calendarYear && thursday.getUTCMonth() + 1 === calendarMonth;
    ranked.push({ score: inMonth ? 0 : 1, year: candidateYear });
  }
  if (ranked.length > 0) {
    ranked.sort((a, b) => a.score - b.score || a.year - b.year);
    return ranked[0].year;
  }
  return calendarYear;
}

/**
 * Today's calendar date as seen in Europe/London - the timezone the backend
 * uses for "current week" (see `ReportingPeriod.current_week_london()` in
 * `dashboards/domain.py`). Returned as a UTC-midnight Date carrying that
 * calendar date, so it can be fed straight into `getIsoWeek` and the other
 * calendar-date helpers in this module.
 *
 * Callers that want "the current ISO week" should use
 * `getIsoWeek(londonToday())` rather than `getIsoWeek(new Date())` - the
 * latter reads back as UTC's calendar date, which drifts from London's by a
 * day for part of the year (BST) and, near midnight, for anyone regardless
 * of DST.
 */
export function londonToday(): Date {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(new Date());
  const part = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return toUtcDate(part('year'), part('month') - 1, part('day'));
}
